import { inject, Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { map } from 'rxjs/operators';
import { rentedPropertyMatchesQuery } from './rented-property-matches.queries';
import type {
  FilterOptions,
  PagedBean,
  PropertyMatchBean
} from '@ui/shared/model';

interface RentedPropertyMatchesResponse {
  propertyApplyProcessRented: PagedBean<PropertyMatchBean>;
}

const EMPTY_PAGE: PagedBean<PropertyMatchBean> = { nodes: [], page: {} };

/**
 * Apollo access for the tenant's rented objects.
 *
 * Two deliberate breaks from the legacy `PropertyMatchesFacade` this replaces:
 *
 * - Plain `query`, not `watchQuery` with a cached `QueryRef` instance field.
 *   The legacy facade was a root singleton whose `refetchRentedMatches()`
 *   reached into whichever `QueryRef` had been created last, so two screens
 *   could clobber each other. Callers now ask the store to reload instead.
 * - No `filter(r => r.dataState === 'complete')`. Apollo v4's `query()`
 *   resolves to `{ data, error }` with no `dataState` at all — that field only
 *   exists on `watchQuery().valueChanges`, whose cache-partial/streaming
 *   emissions were the sole reason the filter existed.
 */
@Injectable({ providedIn: 'root' })
export class RentedPropertyMatchesFacade {
  readonly #apollo = inject(Apollo);

  getRentedPropertyMatches(filter: FilterOptions) {
    return this.#apollo
      .query<RentedPropertyMatchesResponse>({
        query: rentedPropertyMatchesQuery,
        variables: { filter },
        fetchPolicy: 'no-cache'
      })
      .pipe(map(res => res.data?.propertyApplyProcessRented ?? EMPTY_PAGE));
  }
}
